import type { ProductCodes } from 'lib/utils/get-product-specific-code';
import type { BookmarkPosition } from 'entities/bookmark';

export const JOURNEY_CHAPTER_COUNT = 4;

// Login exits land on /account/home with this marker so the page can tell a
// fresh login from a deliberate visit and route Journey members onward.
export const POST_LOGIN_ENTRY_PARAM = 'entry';
export const POST_LOGIN_ENTRY_VALUE = 'login';
export const POST_LOGIN_HOME_ROUTE = `/account/home?${POST_LOGIN_ENTRY_PARAM}=${POST_LOGIN_ENTRY_VALUE}`;

// Where a member with no subscriptions (a one-time purchaser) is sent to
// subscribe to DS-01. Also the landing page for the LCM 21-day email, so it
// lives outside /account and needs no login.
export const SUBSCRIPTION_BENEFITS_ROUTE = '/subscription-benefits';

const FRAUD_REVIEW_ROUTE = '/account/subscriptions';

export const isFraudulentUser = (
  user: { fraudulentStatus?: string | null } | null | undefined,
): boolean => Boolean(user?.fraudulentStatus?.toLowerCase().includes('fraud'));

// Fraud-flagged members stay on the subscriptions page; a member whose
// subscriptions have loaded as empty goes to the DS-01 subscribe page. While
// subscriptions are still unknown the member lands on home, which finishes
// the routing once they resolve (see usePostLoginJourneyRedirect).
export const getPostLoginRoute = (
  user: { fraudulentStatus?: string | null } | null | undefined,
  subscriptions: unknown[] | null | undefined,
): string => {
  if (isFraudulentUser(user)) return FRAUD_REVIEW_ROUTE;
  if (Array.isArray(subscriptions) && subscriptions.length === 0) {
    return SUBSCRIPTION_BENEFITS_ROUTE;
  }
  return POST_LOGIN_HOME_ROUTE;
};

// MVP is DS-01 only — see /account/journey/[product]/[chapter].tsx for the
// full product/chapter matrix. DVS-01 joins once the next product wave ships.
export const DS01_PRODUCT_SLUG = 'ds-01';

export const getJourneyChapterRoute = (chapterNumber: number): string =>
  `/account/journey/${DS01_PRODUCT_SLUG}/ch${chapterNumber}`;

// The routing slug (above) and the API-facing product code (bookmarks,
// subscriptions) name the same product differently — this is the map
// between them, same shape as CHAPTERS_BY_PRODUCT in the dynamic route.
export const PRODUCT_CODE_BY_SLUG: Record<string, ProductCodes> = {
  [DS01_PRODUCT_SLUG]: 'syn',
};

export type ChapterStatus = 'active' | 'completed' | 'upcoming';

// Where "Your Journey" nav clicks land once the member has finished their
// unlocked chapter — see getJourneyDestination.
export const JOURNEY_EVERGREEN_ROUTE = '/account/journey';

// Mirrors seed_core's get_member_experience_chapter thresholds: each chapter
// unlocks this many days after the subscription's initial delivery, where a
// "day" is a full elapsed 24h period (Python timedelta.days semantics).
export const CHAPTER_UNLOCK_DAYS_SINCE_DELIVERY = [0, 14, 30, 60] as const;

// max pct a card can reach before we just fill the progression all the way
const MAX_CHAPTER_FILL = 0.75;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

// Day 1 is the purchase moment itself; each full 24h after that adds one.
// Missing/invalid dates read as day 1 rather than an empty bar.
export const getDaysSincePurchase = (
  purchaseDate: string | undefined,
  now: Date = new Date(),
): number => {
  if (!purchaseDate) return 1;
  const purchased = new Date(purchaseDate);
  if (Number.isNaN(purchased.getTime())) return 1;
  const elapsedDays = Math.floor(
    (now.getTime() - purchased.getTime()) / MS_PER_DAY,
  );
  return Math.max(elapsedDays, 0) + 1;
};

// Full elapsed 24h periods since the given date — the same day count the
// server derives chapters from. Missing/invalid/future dates read as 0.
export const getElapsedDaysSince = (
  dateIso: string | null | undefined,
  now: Date = new Date(),
): number => {
  if (!dateIso) return 0;
  const then = new Date(dateIso);
  if (Number.isNaN(then.getTime())) return 0;
  return Math.max(Math.floor((now.getTime() - then.getTime()) / MS_PER_DAY), 0);
};

const getChapterDurationDays = (chapterNumber: number): number => {
  const start = CHAPTER_UNLOCK_DAYS_SINCE_DELIVERY[chapterNumber - 1];
  const next = CHAPTER_UNLOCK_DAYS_SINCE_DELIVERY[chapterNumber];
  if (next !== undefined) return next - start;
  return start - CHAPTER_UNLOCK_DAYS_SINCE_DELIVERY[chapterNumber - 2];
};

// Time-only progress through one chapter's window: the unlock day counts as
// day 1, and the bar clamps to full at 3/4 of the chapter's duration.
export const getTimeBasedChapterProgress = (
  daysSinceDelivery: number,
  chapterNumber: number,
): number => {
  const start = CHAPTER_UNLOCK_DAYS_SINCE_DELIVERY[chapterNumber - 1];
  if (start === undefined) return 0;
  const fillDays = getChapterDurationDays(chapterNumber) * MAX_CHAPTER_FILL;
  const dayWithinChapter = daysSinceDelivery - start + 1;
  const percent = Math.round((dayWithinChapter / fillDays) * 100);
  return Math.min(Math.max(percent, 0), 100);
};

// Past chapters read as completed, the member's current chapter is active, and
// later chapters are still upcoming.
export const getChapterStatus = (
  chapterNumber: number,
  currentChapter: number,
): ChapterStatus => {
  if (chapterNumber < currentChapter) return 'completed';
  if (chapterNumber === currentChapter) return 'active';
  return 'upcoming';
};

// Only the piece of a Bookmark this needs — real API responses (typed as the
// full Bookmark) satisfy this structurally.
interface BookmarkChapterSource {
  lastVisited: Pick<BookmarkPosition, 'chapter'>;
}

// Bookmark data is an unvalidated API response (string | number, no bounds) —
// this is the one place that turns it into a trustworthy chapter number or
// null, so callers never have to guard against NaN or an out-of-range value.
export const getBookmarkChapter = (
  bookmark: BookmarkChapterSource | null | undefined,
): number | null => {
  const chapter = Number(bookmark?.lastVisited?.chapter);
  if (!Number.isInteger(chapter)) return null;
  if (chapter < 1 || chapter > JOURNEY_CHAPTER_COUNT) return null;
  return chapter;
};

// Same trust boundary as getBookmarkChapter, for the section piece. Sections
// are 1-based and counts vary per chapter, so no upper bound applies.
interface BookmarkSectionSource {
  lastVisited: Pick<BookmarkPosition, 'section'>;
}

export const getBookmarkSection = (
  bookmark: BookmarkSectionSource | null | undefined,
): number | null => {
  const section = Number(bookmark?.lastVisited?.section);
  if (!Number.isInteger(section)) return null;
  if (section < 1) return null;
  return section;
};

// A bookmark's position with wire values normalized to numbers so positions
// can be ordered.
export type BookmarkedPosition = {
  chapter: number;
  section: number;
  chapterCompleted: boolean;
};

interface BookmarkPositionSource {
  lastVisited: Pick<
    BookmarkPosition,
    'chapter' | 'section' | 'chapterCompleted'
  >;
}

export const positionFromBookmark = (
  bookmark: BookmarkPositionSource | null | undefined,
): BookmarkedPosition | null => {
  const chapter = getBookmarkChapter(bookmark);
  if (chapter === null) return null;
  return {
    chapter,
    section: getBookmarkSection(bookmark) ?? 0,
    chapterCompleted: Boolean(bookmark?.lastVisited?.chapterCompleted),
  };
};

// The bookmark only advances: a later chapter, a later section within the
// chapter, or completing it. A completed chapter is beyond all its sections.
export const isPositionAhead = (
  current: BookmarkedPosition | null,
  next: BookmarkedPosition,
): boolean => {
  if (current === null) return true;
  if (next.chapter !== current.chapter) return next.chapter > current.chapter;
  if (current.chapterCompleted) return false;
  if (next.chapterCompleted) return true;
  return next.section > current.section;
};

// "Your Journey" follows the member's furthest unlocked chapter until they've
// read it through, then rests on the Evergreen hub. A bookmark ahead of the
// unlocked chapter has nowhere to go either — JourneyProvider bounces
// 'upcoming' chapters — so it falls back to the hub as well.
export const getJourneyDestination = (
  currentChapter: number,
  bookmark: BookmarkedPosition | null,
): string => {
  const hasFinishedCurrentChapter =
    bookmark !== null &&
    (bookmark.chapter > currentChapter ||
      (bookmark.chapter === currentChapter && bookmark.chapterCompleted));
  return hasFinishedCurrentChapter
    ? JOURNEY_EVERGREEN_ROUTE
    : getJourneyChapterRoute(currentChapter);
};
